import styled from 'styled-components';
import { theme } from '@theme';

// Conteneurs principaux
export const FormSection = styled.div`
  margin-bottom: ${theme.spacing['2xl']};
`;

export const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  svg {
    font-size: 24px;
    color: ${theme.colors.primary};
  }
`;

export const SubSectionTitle = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: ${theme.spacing.xl} 0 ${theme.spacing.md} 0;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGridThree = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  margin-bottom: ${theme.spacing.lg};
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

export const Label = styled.label<{ $required?: boolean }>`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};

  ${props => props.$required && `
    &::after {
      content: ' *';
      color: ${theme.colors.error};
    }
  `}
`;

export const HelpText = styled.p`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}70;
  margin: ${theme.spacing.xs} 0 0 0;
  font-style: italic;
`;

export const InfoBox = styled.div<{ $type?: 'info' | 'warning' | 'success' }>`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  background: ${props =>
    props.$type === 'warning' ? theme.colors.warning + '15' :
    props.$type === 'success' ? theme.colors.success + '15' :
    theme.colors.info + '15'
  };
  border-left: 3px solid ${props =>
    props.$type === 'warning' ? theme.colors.warning :
    props.$type === 'success' ? theme.colors.success :
    theme.colors.info
  };

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${props =>
      props.$type === 'warning' ? theme.colors.warning :
      props.$type === 'success' ? theme.colors.success :
      theme.colors.info
    };
  }

  p {
    margin: 0;
    font-size: ${theme.typography.sizes.sm};
    color: ${theme.colors.text};
  }
`;

// Inputs
export const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? theme.colors.error + '20' : theme.colors.primary + '20'};
  }

  &:disabled {
    background: ${theme.colors.surface}40;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.colors.text}40;
  }
`;

export const Select = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  transition: all ${theme.transitions.fast};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? theme.colors.error + '20' : theme.colors.primary + '20'};
  }

  &:disabled {
    background: ${theme.colors.surface}40;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing.md};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  resize: vertical;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? theme.colors.error + '20' : theme.colors.primary + '20'};
  }

  &:disabled {
    background: ${theme.colors.surface}40;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.colors.text}40;
  }
`;

export const ErrorText = styled.span`
  display: block;
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.error};
`;

// Toggle Switch
export const ToggleSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

export const ToggleInput = styled.input.attrs({ type: 'checkbox' })`
  position: relative;
  width: 48px;
  height: 24px;
  appearance: none;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:checked {
    background: ${theme.colors.primary};
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: transform ${theme.transitions.fast};
  }

  &:checked::before {
    transform: translateX(24px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ToggleLabel = styled.span`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

// Checkbox
export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${theme.colors.primary};
  }
`;

// Radio Buttons
export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

export const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${theme.colors.primary};
  }
`;

// Dynamic entries (Tontines, Crédits, etc.)
export const DynamicEntryContainer = styled.div`
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
  position: relative;
  background: ${theme.colors.background};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: white;
  }
`;

export const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

export const EntryTitle = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const EntryNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${theme.colors.primary};
  color: white;
  border-radius: 50%;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.bold};
`;

export const RemoveButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.error};
  color: ${theme.colors.error};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error};
    color: white;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 16px;
  }
`;

export const AddButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.accent}10;
  border: 2px dashed ${theme.colors.accent};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.accent};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  margin-top: ${theme.spacing.lg};

  &:hover {
    background: ${theme.colors.accent};
    color: white;
    border-style: solid;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 20px;
  }
`;

// Calcul affiché
export const CalculatedValue = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primary}10;
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.primary};
  margin-top: ${theme.spacing.md};
`;

export const CalculatedLabel = styled.span`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text}80;
  margin-bottom: ${theme.spacing.xs};
`;

export const CalculatedAmount = styled.span`
  display: block;
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
`;

// Barre de progression
export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin: ${theme.spacing.md} 0;
`;

export const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background: ${props =>
    props.$percentage < 25 ? theme.colors.error :
    props.$percentage < 50 ? theme.colors.warning :
    props.$percentage < 75 ? theme.colors.info :
    theme.colors.success
  };
  transition: width ${theme.transitions.normal};
`;

export const ProgressText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  margin: 0;
  text-align: right;
`;
